import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  BadgeDollarSign,
  BarChart2,
  Bell,
  BellDot,
  BellRing,
  Book,
  Box,
  Calendar,
  CalendarClock,
  CalendarCog,
  Clock,
  CornerUpLeft,
  Cpu,
  DollarSign,
  CreditCard,
  Repeat,
  FileText,
  Inbox,
  List,
  Mail,
  Palette,
  Receipt,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Slash,
  Sparkles,
  StickyNote,
  Store,
  UserCheck,
  Users,
  Wallet,
  Waypoints,
  Megaphone,
  Filter,
  ClipboardList,
  ClipboardPen,
  ClipboardCheck,
  Tags,
} from 'lucide-react'

export interface NavItem {
  /** Router path. */
  href: string
  label: string
  icon: LucideIcon
  /** One-line explanation of what this screen shows, used on the mobile drawer. */
  hint?: string
  /** Renders the unread-conversation count instead of a static badge. */
  badge?: 'inbox'
  /** Name on a section's tab strip, when the section heading already says the rest. */
  tabLabel?: string
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

/**
 * One line of the main menu. A section holding several screens shows them as
 * tabs across the top of each (DataScreen draws them); the line opens the first.
 */
export interface NavSection {
  label: string
  icon: LucideIcon
  hint?: string
  badge?: 'inbox'
  items: NavItem[]
}

// New sale sits at the VERY top, above even the calendar (user request
// 2026-08-19: "a new section on the very top in the left menu called new sale").
// It is the till: selling a package — and starting a membership — with the money
// recorded in the same motion. Services are deliberately not sold here: a
// service is paid for through its booking.
export const NEW_SALE_ITEM: NavItem = {
  href: '/new-sale',
  label: 'New sale',
  icon: BadgeDollarSign,
  hint: 'Sell a package or start a membership',
}

// The calendar is the operator's morning screen and the post-login landing. Chat
// is deliberately NOT a menu item (user request 2026-08-10): the assistant is a
// slide-out panel opened from its floating button, not a destination.
export const CALENDAR_ITEM: NavItem = {
  href: '/schedule',
  label: 'Calendar',
  icon: Calendar,
  hint: "Today's bookings, day by day",
}

const INBOX_ITEM: NavItem = {
  href: '/inbox', label: 'Inbox', icon: Inbox, hint: 'Customer conversations', badge: 'inbox',
}

const OVERVIEW_ITEM: NavItem = {
  href: '/overview', label: 'Overview', icon: BarChart2, hint: 'Day summary, totals and trends',
}

// The main menu is the work of the day; everything set up once and rarely
// touched lives in Settings (user decision 2026-10-02, replacing 31 menu lines).
export const MAIN_SECTIONS: NavSection[] = [
  { label: 'Calendar', icon: Calendar, hint: CALENDAR_ITEM.hint, items: [CALENDAR_ITEM] },
  { label: 'Inbox', icon: Inbox, hint: INBOX_ITEM.hint, badge: 'inbox', items: [INBOX_ITEM] },
  {
    label: 'Customers',
    icon: Users,
    hint: 'Records, notes and intake answers',
    items: [
      { href: '/customers', label: 'Customers', tabLabel: 'All customers', icon: Users, hint: 'Records, profiles, notes and progress' },
      { href: '/intake-submissions', label: 'Intake submissions', icon: ClipboardCheck, hint: 'Who has answered, and clearance' },
    ],
  },
  {
    label: 'Sales',
    icon: Wallet,
    hint: 'Bookings, payments and memberships',
    items: [
      { href: '/bookings', label: 'Bookings', icon: Book, hint: 'Every appointment, past and future' },
      { href: '/payments', label: 'Payments', icon: DollarSign, hint: 'Money received, per booking' },
      { href: '/refunds', label: 'Refunds', icon: CornerUpLeft, hint: 'Refunds and credits issued' },
      { href: '/payment-requests', label: 'Payment requests', icon: CreditCard, hint: 'Card payment links, and which were paid' },
      { href: '/memberships', label: 'Memberships', icon: Repeat, hint: 'Recurring plans, and any whose card failed' },
      { href: '/package-sales', label: 'Package sales', icon: ShoppingBag, hint: 'Packages sold, and the credits left on each' },
    ],
  },
  {
    label: 'Marketing',
    icon: Megaphone,
    hint: 'Campaigns, audiences and sequences',
    items: [
      // Write the email, choose who, send or schedule — all in the one wizard.
      // A campaign owns its email; reuse is duplicating a previous campaign.
      { href: '/campaigns', label: 'Campaigns', icon: Megaphone, hint: 'Write a one-off email and send it to a group' },
      { href: '/segments', label: 'Audiences', icon: Filter, hint: 'Saved groups of customers to email' },
      { href: '/sequences', label: 'Sequences', icon: Send, hint: 'Drip campaigns and who is enrolled' },
      { href: '/sequence-log', label: 'Sequence sends', icon: Waypoints, hint: 'Every campaign step sent or skipped' },
    ],
  },
  { label: 'Overview', icon: BarChart2, hint: OVERVIEW_ITEM.hint, items: [OVERVIEW_ITEM] },
]

/** The menu line into the Settings area; while inside it the menu lists SETTINGS_GROUPS. */
export const SETTINGS_ENTRY: NavItem = {
  href: '/settings',
  label: 'Settings',
  icon: Settings,
  hint: 'Catalogue, hours, messages and team',
}

// Grouped by what the setting is ABOUT. Every tenant table is reachable from
// exactly one item, here or in MAIN_SECTIONS.
export const SETTINGS_GROUPS: NavGroup[] = [
  {
    title: 'Business',
    items: [
      { href: '/settings', label: 'General', icon: Store, hint: 'Business details, plan and account' },
      { href: '/settings/booking-rules', label: 'Booking rules', icon: CalendarCog, hint: 'Notice periods and multi-treatment visits' },
      { href: '/settings/payments', label: 'Payment settings', icon: CreditCard, hint: 'Which way sessions are paid for by default' },
      { href: '/settings/invoices', label: 'Invoices & tax', icon: Receipt, hint: 'ABN, address and GST on receipts' },
      { href: '/settings/branding', label: 'Branding', icon: Palette, hint: 'Logo, colour and what clients see' },
      { href: '/settings/messaging', label: 'Email & SMS', icon: Mail, hint: 'The address and numbers messages come from' },
      { href: '/settings/assistant', label: 'AI assistant', icon: Sparkles, hint: 'Service shortcuts and note styles' },
    ],
  },
  {
    title: 'Your account',
    items: [
      { href: '/settings/security', label: 'Sign-in & security', icon: ShieldCheck, hint: 'Your password and authenticator app' },
      { href: '/settings/notifications', label: 'Your notifications', icon: BellDot, hint: 'Alerts on this device' },
    ],
  },
  {
    title: 'Catalogue',
    items: [
      { href: '/services', label: 'Services', icon: List, hint: 'Treatments, prices and durations' },
      { href: '/service-categories', label: 'Categories', icon: Tags, hint: 'Headings the menu is grouped under' },
      { href: '/packages', label: 'Packages', icon: Box, hint: 'Multi-session bundles you sell' },
      { href: '/resources', label: 'Resources', icon: Cpu, hint: 'Machines, rooms and staff slots' },
      // Beside the treatments it records, not under Messages with the intake form:
      // a treatment card is the business's own record of what it did in the room.
      { href: '/treatment-cards', label: 'Treatment cards', icon: ClipboardPen, hint: 'What you record about a session' },
    ],
  },
  {
    title: 'Availability',
    items: [
      { href: '/hours', label: 'Business hours', icon: Clock, hint: 'Weekly opening times' },
      { href: '/schedules', label: 'Resource schedules', icon: SlidersHorizontal, hint: 'When each machine is available' },
      // Time blocks and calendar notes sit together: the two non-booking things on
      // the calendar, and only one of them stops bookings.
      { href: '/time-blocks', label: 'Time blocks', icon: Slash, hint: 'Closures and one-off blocks' },
      { href: '/calendar-notes', label: 'Calendar notes', icon: StickyNote, hint: 'Day memos that leave the time bookable' },
      { href: '/open-slots', label: 'Open slots', icon: CalendarClock, hint: 'What a customer could book right now' },
    ],
  },
  {
    title: 'Messages',
    items: [
      { href: '/templates', label: 'Message templates', icon: FileText, hint: 'Reminder and marketing copy' },
      { href: '/reminder-log', label: 'Reminder log', icon: BellRing, hint: 'Appointment reminders actually sent' },
      { href: '/notifications', label: 'Notification schedules', icon: Bell, hint: 'Scheduled digests and alerts' },
      { href: '/notification-log', label: 'Notification log', icon: Activity, hint: 'What was actually delivered' },
      { href: '/intake-forms', label: 'Intake forms', icon: ClipboardList, hint: 'The questionnaire new clients answer' },
    ],
  },
  {
    title: 'Team',
    items: [
      { href: '/team', label: 'Team', icon: UserCheck, hint: 'Who can sign in and manage the business' },
    ],
  },
]

/** Every destination — used for active-route matching and the assistant's screen context. */
export const ALL_NAV_ITEMS: NavItem[] = [
  NEW_SALE_ITEM,
  ...MAIN_SECTIONS.flatMap((s) => s.items),
  ...SETTINGS_GROUPS.flatMap((g) => g.items),
]

// Longest-prefix match so detail routes light up their parent
// (/customer/abc → Customers, /booking/abc → Bookings).
const DETAIL_PARENTS: Record<string, string> = {
  '/customer/': '/customers',
  '/template/': '/templates',
  '/notification/': '/notifications',
  '/booking/': '/bookings',
  '/conversation/': '/inbox',
  '/sequence/': '/sequences',
  '/campaign/': '/campaigns',
  '/segment/': '/segments',
  '/service/': '/services',
  '/package/': '/packages',
  '/resource/': '/resources',
  '/intake-form/': '/intake-forms',
  '/treatment-card/': '/treatment-cards',
  '/intake-submission/': '/intake-submissions',
}

/** The destination a path belongs to, or null for a path no menu item covers. */
export function knownHref(pathname: string): string | null {
  for (const [prefix, parent] of Object.entries(DETAIL_PARENTS)) {
    if (pathname.includes(prefix)) return parent
  }
  if (pathname === '/' || pathname === '/index') return CALENDAR_ITEM.href
  return ALL_NAV_ITEMS.find((i) => i.href === pathname)?.href ?? null
}

/** The menu item to light up. The calendar is home, so unknown paths light it. */
export function activeHref(pathname: string): string {
  return knownHref(pathname) ?? CALENDAR_ITEM.href
}

/** What "Back" out of Settings should be called for a destination outside it. */
export function backLabel(href: string | null): string {
  if (href === NEW_SALE_ITEM.href) return NEW_SALE_ITEM.label
  return (href && sectionFor(href)?.label) || CALENDAR_ITEM.label
}

/** The main-menu section a destination belongs to, or null for one in Settings (or New sale). */
export function sectionFor(href: string): NavSection | null {
  return MAIN_SECTIONS.find((s) => s.items.some((i) => i.href === href)) ?? null
}

/** True when a destination lives in the Settings area, so the menu shows the settings list. */
export function isSettingsHref(href: string): boolean {
  return SETTINGS_GROUPS.some((g) => g.items.some((i) => i.href === href))
}
